import { useCallback, useState } from "react"
import { gql } from "~/__generated__"
import { EVENTS, trackEvent } from "~/common/analytics"
import { AttributionData, getLastTouchAttribution } from "~/common/attribution"
import {
  getFlagEmoji,
  isValidPhoneNumber,
  parsedPhoneNumber,
  PhoneNumberValue,
  US_COUNTRY_CODE,
} from "~/common/phone-number"
import { isMobile } from "~/common/platform"
import { useRecaptcha } from "~/hooks/use-recaptcha"
import { useToast } from "~/ui/use-toast"
import { downloadPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { getInviterData } from "~/share/share-embedded-data"

const DEFAULT_PHONE_VALUE: PhoneNumberValue = {
  number: "",
  callingCode: "1",
  countryCode: US_COUNTRY_CODE,
  flag: getFlagEmoji(US_COUNTRY_CODE),
}

const SEND_DOWNLOAD_APP_LINK = gql(/* GraphQL */ `
  mutation SendDownloadAppLink($input: SendDownloadAppLinkInput!) {
    sendDownloadAppLink(input: $input) {
      success
      errors
    }
  }
`)

const SEND_FRIEND_INVITE_LINK = gql(/* GraphQL */ `
  mutation SendFriendInviteLink($input: SendFriendInviteLinkInput!) {
    sendFriendInviteLink(input: $input) {
      success
      errors
    }
  }
`)

const SEND_ROOM_SHARE_LINK = gql(/* GraphQL */ `
  mutation SendRoomShareLink($input: SendRoomShareLinkInput!) {
    sendRoomShareLink(input: $input) {
      success
      errors
    }
  }
`)

type MutationHandler = (params: {
  phoneNumber: string
  attribution: AttributionData | null
}) => Promise<void>

export type PhoneSubmissionParams = {
  trackingData?: Record<string, unknown>
  successTitle?: string
  successMessage?: string
}

const useBasePhoneSubmission = () => {
  const [phoneValue, setPhoneValue] = useState<PhoneNumberValue>(DEFAULT_PHONE_VALUE)
  const { toast } = useToast()

  const submitPhoneNumber = useCallback(
    async ({
      trackingData,
      successMessage,
      successTitle = "Success",
      handleSubmit,
    }: PhoneSubmissionParams & { handleSubmit: MutationHandler }) => {
      if (!isValidPhoneNumber(phoneValue)) {
        toast({
          title: "Invalid Phone Number",
          description: "Please enter a valid phone number",
          variant: "destructive",
        })
        return false
      }

      const cleanPhoneNumber = parsedPhoneNumber(phoneValue)
      const attribution = getLastTouchAttribution()

      const trackItemPressed = (success: boolean, label?: string) => {
        trackEvent(EVENTS.ITEM_PRESSED, {
          ...trackingData,
          phone_number: cleanPhoneNumber,
          attribution_data: attribution, // Add attribution for GTM
          success,
          label,
        })
      }

      const prevPhoneValue = phoneValue
      setPhoneValue((prevValue) => ({
        ...prevValue,
        number: "",
      }))

      try {
        await handleSubmit({ phoneNumber: cleanPhoneNumber, attribution })
        const defaultSuccessMessage = isMobile()
          ? "Download the app to join."
          : "Check your texts for a link to download the app."

        toast({
          title: successTitle,
          description: successMessage || defaultSuccessMessage,
          duration: 15000,
        })
        trackItemPressed?.(true)

        setTimeout(() => {
          window.location.href = downloadPath({})
        }, 3000)

        return true
      } catch (error) {
        setPhoneValue(prevPhoneValue)
        const errorMessage = error instanceof Error ? error.message : "An error occurred"
        // TODO: Replace with sentry after sentry integration on web
        console.error("Phone submission error:", error)
        trackItemPressed?.(false, errorMessage)

        setTimeout(() => {
          window.location.href = downloadPath({})
        }, 3000)

        return false
      }
    },
    [phoneValue, toast]
  )

  return { phoneValue, setPhoneValue, submitPhoneNumber }
}

const handleMutationFailure = (message?: string) => {
  const errorMessage = message ?? "Unable to send SMS. Please try again."
  throw new Error(errorMessage)
}

export const useDownloadAppPhoneSubmission = () => {
  const [sendAppDownloadLink, { loading }] = useSafeMutation(SEND_DOWNLOAD_APP_LINK)
  const { verifyRecaptcha } = useRecaptcha()
  const handleSubmit: MutationHandler = useCallback(
    async ({ phoneNumber, attribution }) => {
      const result = await sendAppDownloadLink({
        variables: {
          input: {
            phoneNumber,
            attributionData: attribution,
            recaptchaToken: await verifyRecaptcha(),
          },
        },
      })

      if (!result.data?.sendDownloadAppLink?.success) {
        handleMutationFailure(result.data?.sendDownloadAppLink?.errors?.[0])
      }
    },
    [sendAppDownloadLink, verifyRecaptcha]
  )
  const { submitPhoneNumber: internalSubmitPhoneNumber, ...handles } = useBasePhoneSubmission()

  const submitPhoneNumber = useCallback(
    (params: PhoneSubmissionParams) => {
      return internalSubmitPhoneNumber({ ...params, handleSubmit })
    },
    [internalSubmitPhoneNumber, handleSubmit]
  )

  return { ...handles, mutationPending: loading, submitPhoneNumber }
}

export const useInviteFriendPhoneSubmission = () => {
  const [sendInviteFriendLink, { loading }] = useSafeMutation(SEND_FRIEND_INVITE_LINK)
  const { verifyRecaptcha } = useRecaptcha()
  const handleSubmit: MutationHandler = useCallback(
    async ({ phoneNumber, attribution }) => {
      const result = await sendInviteFriendLink({
        variables: {
          input: {
            phoneNumber,
            attributionData: attribution,
            recaptchaToken: await verifyRecaptcha(),
            inviterId: getInviterData()?.id,
          },
        },
      })

      if (!result.data?.sendFriendInviteLink?.success) {
        handleMutationFailure(result.data?.sendFriendInviteLink?.errors?.[0])
      }
    },
    [sendInviteFriendLink, verifyRecaptcha]
  )
  const { submitPhoneNumber: internalSubmitPhoneNumber, ...handles } = useBasePhoneSubmission()

  const submitPhoneNumber = useCallback(
    (params: PhoneSubmissionParams) => {
      return internalSubmitPhoneNumber({ ...params, handleSubmit })
    },
    [internalSubmitPhoneNumber, handleSubmit]
  )

  return { ...handles, mutationPending: loading, submitPhoneNumber }
}

export const useRoomSharePhoneSubmission = () => {
  const [sendRoomShareLink, { loading }] = useSafeMutation(SEND_ROOM_SHARE_LINK)
  const { verifyRecaptcha } = useRecaptcha()

  const { submitPhoneNumber: internalSubmitPhoneNumber, ...handles } = useBasePhoneSubmission()

  const submitPhoneNumber = useCallback(
    ({
      roomId,
      additionalRoomIds,
      ...params
    }: PhoneSubmissionParams & { roomId: string; additionalRoomIds?: string[] }) => {
      const handleSubmit: MutationHandler = async ({ phoneNumber, attribution }) => {
        const result = await sendRoomShareLink({
          variables: {
            input: {
              phoneNumber,
              roomId,
              additionalRoomIds,
              inviterId: getInviterData()?.id,
              attributionData: attribution,
              recaptchaToken: await verifyRecaptcha(),
            },
          },
        })

        if (!result.data?.sendRoomShareLink?.success) {
          handleMutationFailure(result.data?.sendRoomShareLink?.errors?.[0])
        }
      }

      return internalSubmitPhoneNumber({ ...params, handleSubmit })
    },
    [internalSubmitPhoneNumber, sendRoomShareLink, verifyRecaptcha]
  )

  return { ...handles, mutationPending: loading, submitPhoneNumber }
}
